<template>
    <main class="p-2 md:p-5 w-full h-full max-h-main flex gap-2 flex-col items-start min-h-main">
        <div class="w-full flex-col">
            <div class="flex gap-2 flex-col sm:flex-row">
                <div class="flex flex-col flex-grow">
                    <table class="text-sm">
                        <tr>
                            <th scope="row" class="text-right text-sm">
                                {{ $t('project.information.generals.nbDaysCCAP') }} :
                            </th>
                            <td class="px-3">
                                <app-number-link
                                    :label="$t('project.information.generals.nbDaysCCAP')"
                                    v-model="project.nbDaysWeatherIssues"
                                    :show-label="false"
                                    @input="saveCCAP($event)"
                                    :disabled="!isAdmin"
                                ></app-number-link>
                            </td>
                        </tr>
                    </table>
                </div>
            </div>
            <div class="w-full">
                <div class="grid sm:grid-rows-1 grid-cols-1 sm:grid-cols-5 gap-4 my-5">
                    <div class="border shadow p-1">
                        <table class="w-full">
                            <tr>
                                <th scope="row" class="text-right text-sm whitespace-nowrap">
                                    {{ $t('project.follow.weatherIssues.reported') }} :
                                </th>
                                <td class="px-3 whitespace-nowrap">
                                    {{ reportedWeatherIssues }} ({{ reportedDays }}J)
                                </td>
                            </tr>
                        </table>
                    </div>

                    <div class="border shadow p-1">
                        <table class="w-full">
                            <tr>
                                <th scope="row" class="text-right text-sm whitespace-nowrap">
                                    {{ $t('project.follow.weatherIssues.validated') }} :
                                </th>
                                <td class="px-3 whitespace-nowrap">
                                    {{ validatedWeatherIssues }} ({{ validatedDays }}J)
                                </td>
                            </tr>
                        </table>
                    </div>
                    <div class="border shadow p-1">
                        <table class="w-full">
                            <tr>
                                <th scope="row" class="text-right text-sm whitespace-nowrap">
                                    {{ $t('project.follow.weatherIssues.rejected') }} :
                                </th>
                                <td class="px-3 whitespace-nowrap">
                                    {{ rejectedWeatherIssues }} ({{ rejectedDays }}J)
                                </td>
                            </tr>
                        </table>
                    </div>
                    <div class="border shadow p-1">
                        <table class="w-full">
                            <tr>
                                <th scope="row" class="text-right text-sm whitespace-nowrap">
                                    {{ $t('project.follow.weatherIssues.toValidate') }} :
                                </th>
                                <td class="px-3 whitespace-nowrap">
                                    {{ weatherIssuesToValidate }} ({{ daysToValidate }}J)
                                </td>
                            </tr>
                        </table>
                    </div>
                    <div class="border shadow p-1">
                        <table class="w-full">
                            <tr>
                                <th scope="row" class="text-right text-sm whitespace-nowrap">
                                    {{ $t('project.follow.weatherIssues.globalImpact') }} :
                                </th>
                                <td class="px-3 whitespace-nowrap">
                                    <span v-if="project.nbDaysWeatherIssues - validatedDays > 0" class="text-green-600">
                                        0J
                                    </span>
                                    <span class="text-red-500" v-else>
                                        +{{ validatedDays - project.nbDaysWeatherIssues }}J
                                    </span>
                                </td>
                            </tr>
                        </table>
                    </div>
                </div>
            </div>
        </div>
        <div class="flex w-full flex-grow flex-col text-xs relative">
            <div v-if="loading" class="flex justify-center">
                <icon-rotate-right class="animate animate-spin"></icon-rotate-right>
            </div>
            <table class="table-fixed" v-else>
                <thead class="bg-white">
                    <tr>
                        <th style="width: 2rem" class="text-left border-r p-1">
                            {{ $t('commons.date') }}
                        </th>
                        <th class="text-left border-r p-1">
                            {{ $t('commons.name') }}
                        </th>
                        <th class="text-left border-r p-1">
                            {{ $t('bundles.bundle') }}
                        </th>
                        <th style="width: 2rem" class="text-left border-r p-1">
                            <span :title="$t('commons.duration')">
                                {{ $t('commons.duration') }}
                            </span>
                        </th>
                        <th style="width: 2rem" class="text-left border-r p-1">
                            <span :title="$t('project.follow.weatherIssues.proof')">
                                <icon-file-pdf-outline width="16" height="16" />
                            </span>
                        </th>
                        <th style="width: 2rem" class="text-left border-r p-1">
                            <span>
                                {{ $t('project.follow.weatherIssues.isValidated') }}
                            </span>
                        </th>
                    </tr>
                </thead>
                <tbody>
                    <template v-for="weatherIssue in filteredItems">
                        <tr class="odd:bg-blue-50 border-t">
                            <td class="border-r p-1">{{ weatherIssue.date | humanizeDate }}</td>
                            <td class="border-r p-1">
                                <div class="flex justify-between items-center">
                                    <router-link
                                        class="hover:underline"
                                        :to="{
                                            name: 'weatherIssue',
                                            params: { ...$route.params, weatherIssueId: weatherIssue.id },
                                        }"
                                    >
                                        {{ weatherIssue.name }}
                                        <span v-if="!weatherIssue.name || weatherIssue.name.trim().length === 0">
                                            ...
                                        </span>
                                    </router-link>
                                </div>
                            </td>
                            <td class="border-r p-1">
                                <div
                                    v-if="weatherIssue.emitters.length === 1"
                                    style="max-width: 10rem"
                                    class="truncate"
                                >
                                    <span class="">
                                        {{ weatherIssue.emitters[0].label }}
                                    </span>
                                </div>
                                <div
                                    class="flex flex-col truncate"
                                    v-if="weatherIssue.emitters.length === 2"
                                    style="max-width: 10rem"
                                >
                                    <span>
                                        {{ weatherIssue.emitters[0].label }}
                                    </span>
                                    <span class="">
                                        {{ weatherIssue.emitters[1].label }}
                                    </span>
                                </div>
                                <span
                                    v-if="weatherIssue.emitters.length >= 3"
                                    :title="weatherIssue.emitters.map((recipient) => recipient.label).join('\n')"
                                >
                                    <app-tips>
                                        {{
                                            weatherIssue.emitters.length +
                                            ' ' +
                                            $t('project.follow.weatherIssues.emitters')
                                        }}
                                    </app-tips>
                                </span>
                            </td>
                            <td class="text-center border-r p-1">
                                {{ weatherIssue.duration }}
                            </td>
                            <td class="text-center border-r p-1">
                                <app-file-link
                                    class="justify-center"
                                    icon="icon-file-pdf-outline"
                                    v-if="weatherIssue.attachments.length > 0"
                                    :url="weatherIssue.attachments[0].url"
                                />
                            </td>
                            <td class="text-center border-r p-1">
                                <div
                                    :title="$t('project.follow.weatherIssues.conclusions.validated')"
                                    v-if="weatherIssue.conclusion === 'validated'"
                                >
                                    <icon-check-circle-outline class="text-green-600 mx-2" width="16" />
                                </div>
                                <div
                                    :title="$t('project.follow.weatherIssues.conclusions.rejected')"
                                    v-else-if="weatherIssue.conclusion === 'rejected'"
                                >
                                    <icon-close-circle-outline class="text-red-700 mx-2" width="16" />
                                </div>
                                <div :title="$t('project.follow.weatherIssues.conclusions.expected')" v-else>
                                    <icon-timelapse class="text-secondary mx-2" width="16" />
                                </div>
                            </td>
                        </tr>
                    </template>
                </tbody>
            </table>
        </div>
    </main>
</template>

<script>
import { filterMatch, getMapById, sortBy } from '@/services/sanitize.service';
import AppSelect from '@/components/appSelect/AppSelect';
import AppQuickActions from '@/components/appQuickActions/AppQuickActions';
import { getWeatherIssues } from './weatherIssues.service';
import AppDateInput from '@/components/appDateInput/AppDateInput';
import AppCheckbox from '@/components/app-checkbox/AppCheckbox';
import AppMultiPicker from '@/components/appMultiPicker/AppMultiPicker';
import AppButton from '@/components/appButton/AppButton';
import { humanizeDate } from '@/filters/dateFilter';
import { getProject } from '@/features/projects/projects.service';
import AppTips from '@/components/app-tips/AppTips';
import IconCheck from '@/icons/IconCheck';
import { combineLatest } from 'rxjs';
import { getBundles, sortBundles } from '@/features/bundles/bundles.service';
import AppInputText from '@/components/appInputText/AppInputText';
import AppBundle from '@/components/app-bundle/appBundle';
import AppFooter from '@/components/appFooter/AppFooter';
import AppPopup from '@/components/app-popup/AppPopup';
import AppNumberLink from '@/components/appNumberLink/AppNumberLink';
import AppMultipleUploadButton from '@/components/app-multipleUploadButton/AppMultipleUploadButton';
import AppFileLink from '@/components/appFileLink/AppFileLink';
import AppUploadButton from '@/components/app-uploadButton/AppUploadButton';
import IconFilePdfOutline from '@/icons/IconFilePdfOutline';
import AppCalendar from '@/components/appCalendar/AppCalendar';
import startOfDay from 'date-fns/startOfDay';
import AppLabel from '@/components/appLabel/AppLabel';
import IconCloseCircleOutline from '@/icons/IconCloseCircleOutline.vue';
import IconCheckCircleOutline from '@/icons/IconCheckCircleOutline.vue';
import IconTimelapse from '@/icons/IconTimelapse.vue';
import { getWeatherIssuesExport } from '@/features/planning/weatherIssues/weatherIssuesExports.service';

export default {
    components: {
        IconTimelapse,
        IconCheckCircleOutline,
        IconCloseCircleOutline,
        AppLabel,
        AppCalendar,
        IconFilePdfOutline,
        AppUploadButton,
        AppFileLink,
        AppMultipleUploadButton,
        AppNumberLink,
        AppPopup,
        AppFooter,
        AppBundle,
        AppInputText,
        IconCheck,
        AppTips,
        AppQuickActions,
        AppCheckbox,
        AppSelect,
        AppDateInput,
        AppMultiPicker,
        AppButton,
    },
    async created() {
        this.subscriptions = [
            combineLatest([
                getWeatherIssues(this.$route.params.projectId),
                getBundles(this.$route.params.projectId),
                getProject(this.$route.params.projectId),
                getWeatherIssuesExport(this.$route.params.projectId, this.$route.params.weatherIssuesExportId),
            ]).subscribe(([weatherIssues, bundles, project, weatherIssuesExport]) => {
                this.project = project;
                this.isAdmin = project.me.allowedFeatures.includes('project_planning');
                const bundleMap = getMapById(bundles);
                this.items = weatherIssues.map((weatherIssue) => {
                    return {
                        ...weatherIssue,
                        filterString: [weatherIssue.name, humanizeDate(weatherIssue.date)].join(),
                        emitters: weatherIssue.emitterIds.map((id) => bundleMap[id]).filter((a) => !!a),
                        isEmitter: project.me.bundleIds.find((bundleId) => weatherIssue.emitterIds.includes(bundleId)),
                    };
                });
                this.bundles = sortBundles(bundles);
                this.restoreFilter(weatherIssuesExport);
                this.restoreSort(weatherIssuesExport);
                this.loading = false;
            }),
        ];
    },
    computed: {
        calendar() {
            return this.items.map((item) => ({ ...item, time: startOfDay(item.date).getTime(), type: 'weatherIssue' }));
        },
        reported() {
            return this.items.reduce((before, weatherIssue) => {
                return before + weatherIssue.duration;
            }, 0);
        },
        weatherIssuesToValidate() {
            return this.items.reduce((before, weatherIssue) => {
                if (!weatherIssue.conclusion) {
                    return before + 1;
                } else {
                    return before;
                }
            }, 0);
        },
        daysToValidate() {
            return this.items.reduce((before, weatherIssue) => {
                if (!weatherIssue.conclusion) {
                    return before + weatherIssue.duration;
                } else {
                    return before;
                }
            }, 0);
        },
        validatedWeatherIssues() {
            return this.items.reduce((before, weatherIssue) => {
                if (weatherIssue.conclusion === 'validated') {
                    return before + 1;
                } else {
                    return before;
                }
            }, 0);
        },
        validatedDays() {
            return this.items.reduce((before, weatherIssue) => {
                if (weatherIssue.conclusion === 'validated') {
                    return before + weatherIssue.duration;
                } else {
                    return before;
                }
            }, 0);
        },
        rejectedWeatherIssues() {
            return this.items.reduce((before, weatherIssue) => {
                if (weatherIssue.conclusion === 'rejected') {
                    return before + 1;
                } else {
                    return before;
                }
            }, 0);
        },
        rejectedDays() {
            return this.items.reduce((before, weatherIssue) => {
                if (weatherIssue.conclusion === 'rejected') {
                    return before + weatherIssue.duration;
                } else {
                    return before;
                }
            }, 0);
        },
        reportedWeatherIssues() {
            return this.items.length;
        },
        reportedDays() {
            return this.items.reduce((before, weatherIssue) => {
                return before + weatherIssue.duration;
            }, 0);
        },
        quickActions() {
            return [
                {
                    name: this.$t('project.follow.weatherIssues.newWeatherIssue'),
                    run: () => this.addWeatherIssue(),
                },
                {
                    name: this.$t('project.follow.weatherIssues.batchAdd'),
                    run: () => this.$refs.batchAddPopup.open(),
                },
            ];
        },
        filteredItems() {
            let result = this.filterFn(this.filterValue);
            if (this.sortKey) {
                result = sortBy(result, (item) => {
                    if (this.sortKey === 'bundle') {
                        return item.index + '_' + item.date?.toISOString();
                    } else if (this.sortKey === 'name') {
                        return item.name + '_' + item.date?.toISOString();
                    } else if (this.sortKey === 'date') {
                        return item.date?.toISOString();
                    }
                });
                if (!this.sortAsc) {
                    result.reverse();
                }
            }
            return result;
        },
    },
    methods: {
        humanizeDate,
        matchString(stringCriteria, item) {
            if (!stringCriteria || stringCriteria.length === 0) {
                return true;
            }
            return stringCriteria.find((criteria) => filterMatch(item.filterString, criteria, true));
        },
        matchAttachment(criteria, item) {
            return (
                criteria.length === 0 ||
                (criteria.includes('withAttachment') && item.attachments.length > 0) ||
                (criteria.includes('noAttachment') && item.attachments.length === 0)
            );
        },
        matchValidation(criteria, item) {
            return (
                criteria.length === 0 ||
                (criteria.includes('validated') && item.conclusion === 'validated') ||
                (criteria.includes('rejected') && item.conclusion === 'rejected') ||
                (criteria.includes('expected') && !item.conclusion)
            );
        },
        matchBundleFilter(item, bundleIds) {
            return item.emitterIds && item.emitterIds.find((id) => bundleIds.includes(id));
        },
        filterFn(filter) {
            const bundleIds = filter.filter((aCriteria) => aCriteria._isBundleCriteria).map((bundle) => bundle.id);
            const stringCriteria = filter
                .filter((aCriteria) => aCriteria._isStringCriteria)
                .map((aCriteria) => aCriteria.content);
            const validationCriteria = filter
                .filter((aCriteria) => aCriteria._validationCriteria)
                .map((criteria) => criteria.id);
            const attachmentCriteria = filter
                .filter((aCriteria) => aCriteria._attachmentCriteria)
                .map((criteria) => criteria.id);
            return this.items.filter((item) => {
                const fullCriteria = {
                    matchBundleFilter: bundleIds.length === 0 || this.matchBundleFilter(item, bundleIds),
                    matchString: this.matchString(stringCriteria, item),
                    matchAttachment: this.matchAttachment(attachmentCriteria, item),
                    matchValidation: this.matchValidation(validationCriteria, item),
                };
                return Object.values(fullCriteria).every((value) => !!value);
            });
        },
        restoreFilter(weatherIssuesExport) {
            this.filterValue = weatherIssuesExport.filter || [];
        },
        restoreSort(weatherIssuesExport) {
            this.sortKey = weatherIssuesExport.sortKey;
            this.sortAsc = weatherIssuesExport.sortAsc;
        },
    },
    data() {
        return {
            file: null,
            loading: true,
            sortKey: 'date',
            sortAsc: true,
            isAdmin: false,
            bundles: [],
            items: [],
            project: {},
            filterValue: [],
        };
    },
};
</script>
